import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Form,
  message,
  Tabs,
  Tooltip,
  Typography,
  Row,
  Col,
  Space
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import session from '../../utils/session';
import PageTourButton from '../guide/PageTourButton';
import { getSettingsSteps } from '../guide/toursteps';
import useProfileStore from '../../stores/useProfileStore';
import ProfileTab from './tabs/ProfileTab';
import SecurityTab from './tabs/SecurityTab';
import NotificationsTab from './tabs/NotificationsTab';
import OfficeHoursTab from './tabs/OfficeHoursTab';
import { useFeature, FeatureLockCard } from '../common/FeatureGate';

const { Text, Title } = Typography;

const UserSettings = ({ location, history }) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [activeTab, setActiveTab] = useState('profile');
  const [glowEffect, setGlowEffect] = useState(false);
  
  const {
    fetchUserData, fetchOfficeHours
  } = useProfileStore();

  // Office Hours is an auto-reply to inbound 1-1 texts — gated on messaging.send.
  const canSendMessages = useFeature('messaging.send');

  useEffect(() => {
    const { id } = session.get('userData') || {};
    if (id) {
       fetchUserData(id, form);
    }
    if (canSendMessages) fetchOfficeHours();

    // Check for pending TFA verification
    const requestId = session.get('tfa_request_id');
    const savedTfaMobile = session.get('tfa_mobile');
    if (requestId && savedTfaMobile) {
      useProfileStore.setState({
        tfaRequestId: requestId,
        tfaMobile: savedTfaMobile,
        tfaCheckModal: true
      });
    }

    // Initialize active tab from URL
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (['profile', 'security', 'notifications', 'office-hours'].includes(tab)) {
       setActiveTab(tab);
    }

    const handleFocusOfficeHours = () => {
      setActiveTab('office-hours');
      // Update URL without reload
      history.push('/settings?tab=office-hours');
      
      // Trigger glow
      setGlowEffect(true);
      setTimeout(() => setGlowEffect(false), 2000); // 2s glow
    };

    const handleFocusProfileTab = () => {
      setActiveTab('profile');
      history.push('/settings?tab=profile');
    };

    window.addEventListener('focusOfficeHours', handleFocusOfficeHours);
    window.addEventListener('focusProfileTab', handleFocusProfileTab);
    return () => {
      window.removeEventListener('focusOfficeHours', handleFocusOfficeHours);
      window.removeEventListener('focusProfileTab', handleFocusProfileTab);
    };
  }, [form, fetchUserData, fetchOfficeHours, location.search, history, canSendMessages]);

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' }}>
      {contextHolder}
            
      <div
        style={{
          background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
          border: '1px solid #d9f7be',
          padding: 20
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={16}>
            <Space direction="vertical" size={6}>
              <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Account Settings <PageTourButton steps={getSettingsSteps()} /></Title>
              <Text style={{ fontSize: 15, color: '#4b5563' }}>
                Configure your profile, notification preferences, and system integrations to tailor the platform to your needs.
              </Text>
              <Space size={16}>
                <Space size={6}>
                  <SettingOutlined style={{ color: '#08979c' }} />
                  <Text type="secondary">Workspace configuration</Text>
                </Space>
                <Space size={6}>
                  <LockOutlined style={{ color: '#08979c' }} />
                  <Text type="secondary">Security & Access</Text>
                </Space>
              </Space>
            </Space>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'right' }}>
            {/* Actions if any */}
          </Col>
        </Row>
      </div>
           
      
      <div style={{ flex: 1, margin: '0', backgroundColor: 'white', border: '1px solid #E6E6E6' }}>
        <Tabs
          className="tour-settings-tabs"
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            history.push(`/settings?tab=${key}`);
          }}
          tabPosition="left"
          style={{ minHeight: '100%' }}
          items={React.useMemo(() => [
            {
              key: 'profile',
              label: 'Profile',
              children: <ProfileTab form={form} messageApi={messageApi} />
            },
            {
              key: 'security',
              label: 'Security',
              children: <SecurityTab form={form} messageApi={messageApi} />
            },
            {
              key: 'notifications',
              label: 'Notifications',
              children: <NotificationsTab form={form} messageApi={messageApi} />
            },
            {
              key: 'office-hours',
              // Office Hours auto-replies to inbound 1-1 texts, so it cannot do
              // anything without messaging.send — show it locked rather than
              // letting the user configure a feature that will never fire.
              label: canSendMessages ? 'Office Hours' : (
                <Tooltip title="Not included in your plan" placement="right">
                  <span style={{ opacity: 0.7 }}>
                    Office Hours
                    <LockOutlined style={{ marginLeft: 6, fontSize: 11 }} />
                  </span>
                </Tooltip>
              ),
              children: canSendMessages
                ? <OfficeHoursTab messageApi={messageApi} glowEffect={glowEffect} />
                : (
                  <div style={{ padding: '32px 40px' }}>
                    <FeatureLockCard
                      featureKey="messaging.send"
                      height={260}
                      title="Office Hours needs Messaging"
                      blurb="Office Hours replies to candidates automatically when they text you after hours, so it needs the ability to send 1-1 messages."
                    />
                  </div>
                )
            }
            // Memoizing items to prevent unnecessary re-renders of heavy tab content
            // when switching active tabs or when parent state updates that doesn't affect tabs.
            // This fixes the lag noticed when switching between tabs.
          ], [form, messageApi, glowEffect, canSendMessages])}
        />
      </div>
    </div>
  );
};

const mapStateToProps = state => ({});
const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(UserSettings));
